import { type ReactNode } from "react";
import { Link, useHistory, type LinkProps } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  IconArrowUpRight,
  IconLink,
  IconPencil,
  IconPlus,
  IconSettings,
  IconShare2,
} from "@tabler/icons-react";
import { displayToast } from "../../../components/app/AppToast";
import { DesktopTopNav } from "../../../components/app/desktopTopNav/DesktopTopNav";
import { DesktopBreadcrumb } from "../../../components/app/desktopTopNav/DesktopBreadcrumb";
import {
  DesktopArticleRow,
  DesktopArticleHeroSkeleton,
} from "../../../components/discovery/DesktopArticleRow";
import { DesktopExploreRail } from "../../../components/discovery/DesktopExploreRail";
import { DesktopSaveButton } from "../../../components/save/DesktopSaveButton";
import { DesktopCollectionCardSkeleton } from "../../../components/cards/DesktopCollectionCard";
import { SaveableCollectionCard } from "../../../domains/playlist/components/SaveableCollectionCard";
import { useCreateCollection } from "../../../domains/playlist/hooks/useCreateCollection";
import Loader from "../../../components/Loader";
import ProfilePicture from "../../../components/ProfilePicture";
import {
  DesktopLatestAiChatCard,
  pickLatestAiChat,
} from "../../../components/profile/LatestAiChatCard";
import ShareSheet from "../../../components/SharedBySheet";
import { Button, buttonVariants } from "../../../components/ui/Button";
import { usePageLinks } from "../../../domains/app/hooks/usePageLinks";
import { useConversationEngine } from "../../../domains/assistant/useConversationEngine";
import { getItemTranslation } from "../../../domains/app/utils/getItemTranslation";
import {
  formatArticleDate,
  renderDate,
} from "../../../domains/app/utils/renderDate";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import { useLoadedAudioId } from "../../../domains/app/contexts/audio.context";
import {
  getArticleTypeText,
  getArticleTypeVariant,
} from "../../../domains/app/utils/getArticleTypeText";
import { authorLine } from "../../../domains/article/utils/authorLine";
import { useShareArticle } from "../../../domains/user/hooks/useShareArticle";
import { gaEventSelectItemArticle } from "../../../tools/analytics/domains/article.analytics";
import { journalImpactFactor } from "../../../domains/journal/utils/journalImpactFactor";
import { shareUrl } from "../../../domains/app/utils/shareUrl";
import {
  useCurrentUser,
  type CurrentUser,
} from "../../../domains/user/hooks/useCurrentUser";
import { useTrackOnMount } from "../../../hooks/use-track-on-mount";
import { useApiQuery, type Api } from "../../../services/api";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import { gaEventSelectItemPlaylist } from "../../../tools/analytics/domains/playlist.analytics";
import {
  gaEventUserViewOwnProfile,
  gaEventUserViewProfile,
} from "../../../tools/analytics/domains/user.analytics";
import {
  ACCOUNT_PAGE,
  openDesktopSettings,
} from "../../../domains/app/stores/desktopSettingsStore";

/** Whose profile the page shows. The viewer's own one carries the editing. */
export type ProfileSubject = { kind: "me" } | { kind: "member"; id: string };

type ProfileUser = Api["get"]["/webapp/private/users/:id"]["output"]["body"];

// Module scope so `useTrackOnMount` sees the same callback on every render.
function trackOwnProfileView(): void {
  gaEventUserViewOwnProfile();
}
function trackMemberProfileView(): void {
  gaEventUserViewProfile("public app");
}

/**
 * The desktop profile (Figma "05 - Profil", D3.profile): an identity header,
 * then the Library in the 680 column and the Explore rail beside it.
 *
 * The column reads what the reader picks back up: the last article read, the
 * collections, the last chat. A section with nothing in it yet is left out
 * (D3.profile.new-user), except the collections, whose empty state is the way
 * to create the first one. The bookmarked and shared collections follow, as on
 * mobile.
 *
 * Editing is not done here: the header and the empty states link into the
 * settings workspace, which is why this page is only mounted where that
 * workspace is up.
 */
export function DesktopProfilePage({
  subject,
}: {
  subject: ProfileSubject;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { currentUser, isOnboarded } = useCurrentUser();
  const targetId =
    subject.kind === "member"
      ? subject.id
      : currentUser === null
        ? null
        : currentUser.id;

  const userQuery = useApiQuery(
    "/webapp/private/users/:id",
    targetId !== null && isOnboarded ? { param: { id: targetId } } : false
  );

  useTrackOnMount(
    "profile-viewed",
    targetId === null
      ? { status: "hidden" }
      : { status: "shown", key: `${subject.kind}:${targetId}` },
    subject.kind === "me" ? trackOwnProfileView : trackMemberProfileView
  );

  return (
    <div className="flex h-full flex-col overflow-hidden bg-ds-white">
      <DesktopTopNav
        context={
          <DesktopBreadcrumb
            back={{ kind: "none" }}
            trail={[]}
            current={t("Profile")}
          />
        }
        trailing={{ kind: "search" }}
        surface="default"
        className={null}
      />
      {/* `idle` is the session still resolving (or onboarding redirecting
          away): there is no profile to show yet, only one on its way. */}
      {currentUser === null ||
      userQuery.state === "idle" ||
      userQuery.state === "pending" ? (
        <Loader expanded />
      ) : userQuery.state === "error" ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-ui-body text-ds-slate-500">
            {t("Something went wrong. Please try again.")}
          </p>
        </div>
      ) : (
        <main className="min-h-0 flex-1 overflow-y-auto">
          <ShareSheet type="profile" />
          <div className="px-10 pt-8 pb-10">
            <div className="mx-auto flex max-w-desktop-block flex-col gap-6">
              <ProfileHeader
                user={userQuery.body}
                subject={subject}
                viewer={currentUser}
              />
              <div className="flex gap-8">
                <div className="flex min-w-0 max-w-desktop-column flex-1 flex-col gap-10 pt-2">
                  <LastReadSection user={userQuery.body} />
                  <CreatedCollectionsSection
                    user={userQuery.body}
                    subject={subject}
                  />
                  {subject.kind === "me" ? <LastChatSection /> : null}
                  <BookmarkedCollectionsSection
                    user={userQuery.body}
                    subject={subject}
                  />
                  {subject.kind === "me" ? <SharedWithMeSection /> : null}
                </div>
                {/* From the 1280 grid only: narrower, the rail would crush the
                    Last read hero into a column a word wide. */}
                <div className="hidden w-desktop-rail shrink-0 border-l border-ds-slate-100 px-6 py-2 xl:block">
                  <div className="sticky top-8">
                    <DesktopExploreRail />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      )}
    </div>
  );
}

// ── Header ───────────────────────────────────────────────────────────────

function ProfileHeader({
  user,
  subject,
  viewer,
}: {
  user: ProfileUser;
  subject: ProfileSubject;
  viewer: CurrentUser;
}): React.JSX.Element {
  const { t } = useTranslation();

  async function shareProfile(): Promise<void> {
    try {
      const outcome = await shareUrl(
        document.title,
        t("Here is a user profile you might be interested in."),
        Promise.resolve(
          `${globalThis.location.origin}/profile/user/${user.id}?shared=${viewer.id}`
        )
      );
      if (outcome === "sent") displayToast(t("Profile shared"), "success");
    } catch (error) {
      console.error("Couldn't share profile.", error);
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  return (
    // Top-aligned: the biography makes the identity taller than the avatar.
    <header className="flex items-start gap-4">
      {/* 128 asks ProfilePicture for its 64px header avatar. */}
      <ProfilePicture
        user={user}
        height={128}
        className="shrink-0"
        badge={null}
      />

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <h1 className="m-0 truncate text-desktop-h1 font-semibold text-ds-black">
          {user.firstname !== null ? user.firstname : t("First Name")}{" "}
          {user.lastname !== null ? user.lastname : t("Last Name")}
        </h1>
        <p className="m-0 text-sm text-ds-slate-700">
          {[user.professionModel, user.mainSpecialtyModel]
            .filter((model) => model !== null)
            .map((model) => getItemTranslation(model))
            .join(" · ")}
        </p>
        {/* The link shares the join date's line (Figma
            "D3.profile.about"): the header spends no row on it. A narrow
            window moves it under the date rather than into the buttons. */}
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-desktop-meta text-ds-slate-700">
          <span className="whitespace-nowrap">
            {t("Joined on {{date}}", {
              date: renderDate(user.meta.creationDate),
            })}
          </span>
          {user.link === null ? null : (
            <>
              <span aria-hidden>·</span>
              <a
                href={user.link}
                target="_blank"
                rel="noreferrer"
                className="flex min-w-0 max-w-full items-center gap-1 font-semibold text-ds-primary underline hover:text-ds-mint-950"
              >
                <IconLink className="size-3.5 shrink-0" stroke={1.5} />
                <span className="truncate">
                  {user.link.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                </span>
                <IconArrowUpRight className="size-3.5 shrink-0" stroke={1.5} />
              </a>
            </>
          )}
        </div>
        {user.bio === null ? null : (
          <p className="m-0 line-clamp-2 pt-1 whitespace-pre-line text-sm text-ds-slate-700">
            {user.bio}
          </p>
        )}
      </div>

      {subject.kind === "me" ? (
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            leftIcon={<IconPencil stroke={1.5} />}
            onClick={() => {
              openDesktopSettings(ACCOUNT_PAGE);
            }}
          >
            {t("Edit profile")}
          </Button>
          <button
            type="button"
            aria-label={t("Settings")}
            onClick={() => {
              openDesktopSettings(ACCOUNT_PAGE);
            }}
            className={buttonVariants({
              variant: "outline",
              size: "icon",
              mode: "iconOnly",
            })}
          >
            <IconSettings className="size-4" stroke={1.5} />
          </button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          leftIcon={<IconShare2 stroke={1.5} />}
          onClick={() => {
            void shareProfile();
          }}
        >
          {t("Share profile")}
        </Button>
      )}
    </header>
  );
}

// ── The Library ──────────────────────────────────────────────────────────

function Section({
  title,
  seeAll,
  children,
}: {
  title: string;
  /** The "See all" link of the section's header, when it has one. */
  seeAll: { label: string; to: LinkProps["to"] } | null;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="m-0 text-desktop-h3 font-semibold text-ds-black">
          {title}
        </h2>
        {seeAll === null ? null : (
          <Link
            to={seeAll.to}
            className="shrink-0 text-desktop-meta font-semibold text-ds-primary no-underline hover:underline"
          >
            {seeAll.label}
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}

/** Left out while there is nothing read yet (D3.profile.new-user). */
function LastReadSection({
  user,
}: {
  user: ProfileUser;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const query = useApiQuery("/webapp/private/users/:id/last-read-article", {
    param: { id: user.id },
  });

  if (query.state === "error") {
    return (
      <Section title={t("Last read")} seeAll={null}>
        <QueryError />
      </Section>
    );
  }
  if (query.state === "pending") {
    return (
      <Section title={t("Last read")} seeAll={null}>
        <DesktopArticleHeroSkeleton />
      </Section>
    );
  }
  if (query.body === null) return null;
  return (
    <Section title={t("Last read")} seeAll={null}>
      <LastReadArticleRow article={query.body} />
    </Section>
  );
}

type LastReadArticleBody = NonNullable<
  Api["get"]["/webapp/private/users/:id/last-read-article"]["output"]["body"]
>;

/** The article as the Explore feed opens on it, with the desktop save popover. */
function LastReadArticleRow({
  article,
}: {
  article: LastReadArticleBody;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { activeLang } = useActiveLang();
  const { currentUser } = useCurrentUser();
  const { articleLink } = usePageLinks(null);
  const { triggerShare } = useShareArticle(AnalyticsItemList.PROFILE);
  const loadedAudioId = useLoadedAudioId();
  const journal = article.journal;
  const cardImage = article.cardImage;
  return (
    <DesktopArticleRow
      onRemove={null}
      layout="hero"
      title={article.title}
      author={authorLine(article.primary_author, article.authors)}
      description={
        article.tldr !== null && article.tldr.length > 0 ? article.tldr : null
      }
      date={formatArticleDate(article.publication_date, activeLang)}
      readingMinutes={null}
      meta={
        article.article_type === null
          ? t("Article")
          : getArticleTypeText(article.article_type)
      }
      metaVariant={getArticleTypeVariant(article.article_type)}
      access={article.isOpenAccess === true ? "open" : "closed"}
      imageUrl={
        cardImage === null
          ? null
          : cardImage.sizes === null
            ? cardImage.url
            : cardImage.sizes.medium.file
      }
      isInPlayer={loadedAudioId === article.id}
      bookmark={
        <DesktopSaveButton
          article={article}
          analyticsListName={AnalyticsItemList.PROFILE}
          size="row"
        />
      }
      onShare={
        currentUser === null
          ? null
          : () => {
              void triggerShare(article, currentUser);
            }
      }
      onClick={() => {
        gaEventSelectItemArticle({
          article,
          listName: AnalyticsItemList.PROFILE,
        });
        history.push(articleLink(article));
      }}
      source={
        journal === null
          ? null
          : {
              kind: "journal",
              name: journal.name,
              impactFactor: journalImpactFactor(journal),
              onClick: () => {
                history.push(`/journal/${journal.uid}`);
              },
            }
      }
    />
  );
}

/** Left out while there is no conversation yet (D3.profile.new-user). */
function LastChatSection(): React.JSX.Element | null {
  const { t } = useTranslation();
  const { gptNewChatPageLink } = usePageLinks(null);
  // The latest conversation of the engine that answers new questions.
  const engine = useConversationEngine();
  const conversations = useApiQuery(
    "/webapp/private/conversations",
    engine === "legacy"
      ? { query: { limit: 1, offset: 0, sort: "newest" } }
      : false
  );
  const assistantConversations = useApiQuery(
    "/assistant/conversations",
    engine === "assistant"
      ? { query: { limit: 1, offset: 0, sort: "newest" } }
      : false
  );
  const sessions = useApiQuery("/webapp/private/chat-with-paper/sessions", {
    query: { limit: 1, offset: 0 },
  });

  const title = t("Last chat with JuisciGPT");
  if (
    conversations.state === "error" ||
    assistantConversations.state === "error" ||
    sessions.state === "error"
  ) {
    return (
      <Section title={title} seeAll={null}>
        <QueryError />
      </Section>
    );
  }
  if (
    engine === "pending" ||
    conversations.state === "pending" ||
    assistantConversations.state === "pending" ||
    sessions.state === "pending"
  )
    return null;

  const legacyDoc =
    conversations.state === "success"
      ? conversations.body.docs.at(0)
      : undefined;
  const assistantDoc =
    assistantConversations.state === "success"
      ? assistantConversations.body.docs.at(0)
      : undefined;
  const session = sessions.body.docs.at(0);
  const chat = pickLatestAiChat(
    legacyDoc !== undefined
      ? { engine: "legacy", doc: legacyDoc }
      : assistantDoc !== undefined
        ? { engine: "assistant", doc: assistantDoc }
        : null,
    session === undefined ? null : session
  );
  if (chat === null) return null;

  return (
    <Section
      title={title}
      // No standalone history page: this lands on the JuisciGPT surface with
      // the history drawer already revealed.
      seeAll={{
        label: t("See all"),
        to: {
          pathname: gptNewChatPageLink(),
          state: { historyArrival: "revealing" },
        },
      }}
    >
      <DesktopLatestAiChatCard chat={chat} />
    </Section>
  );
}

/** Two collections to a row, the width of the 680 column. */
function CollectionGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-8 pt-1">{children}</div>;
}

/**
 * The first two collections the profile's owner created, the rest behind
 * "See all". The viewer's own empty grid offers to create the first one.
 */
function CreatedCollectionsSection({
  user,
  subject,
}: {
  user: ProfileUser;
  subject: ProfileSubject;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const createCollection = useCreateCollection(user.id);
  const query = useApiQuery("/webapp/private/playlists/user/:userId", {
    param: { userId: user.id },
  });

  const title =
    subject.kind === "me"
      ? t("Collections I created")
      : t("{{name}}'s collections", {
          name: user.firstname === null ? "" : user.firstname,
        });

  if (query.state === "pending") {
    return (
      <Section title={title} seeAll={null}>
        <CollectionGrid>
          <DesktopCollectionCardSkeleton />
          <DesktopCollectionCardSkeleton />
        </CollectionGrid>
      </Section>
    );
  }
  if (query.state === "error") {
    return (
      <Section title={title} seeAll={null}>
        <QueryError />
      </Section>
    );
  }

  const collections = query.body;
  if (collections.length === 0) {
    return (
      <Section title={title} seeAll={null}>
        {subject.kind === "me" ? (
          <CollectionGrid>
            <button
              type="button"
              onClick={() => {
                void createCollection([]);
              }}
              className="flex h-61 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-ds-slate-100 bg-ds-moss-50 text-sm text-ds-slate-700 transition-colors hover:border-ds-slate-300"
            >
              <IconPlus className="size-5" stroke={1.5} />
              {t("Create a collection")}
            </button>
          </CollectionGrid>
        ) : (
          <p className="m-0 text-desktop-meta text-ds-slate-600">
            {t("No playlist for this user")}
          </p>
        )}
      </Section>
    );
  }

  return (
    <Section
      title={title}
      seeAll={{
        label: t("See all {{count}}", { count: collections.length }),
        to:
          subject.kind === "me"
            ? "/profile/playlist"
            : `/profile/user/${user.id}/playlist`,
      }}
    >
      <CollectionGrid>
        {collections.slice(0, 2).map((playlist) => (
          <SaveableCollectionCard
            key={playlist.id}
            playlist={playlist}
            listName={AnalyticsItemList.PROFILE}
            onOpen={() => {
              gaEventSelectItemPlaylist({
                playlist,
                listName: AnalyticsItemList.PROFILE,
              });
              history.push(`/profile/playlist/${playlist.id}`);
            }}
          />
        ))}
      </CollectionGrid>
    </Section>
  );
}

function BookmarkedCollectionsSection({
  user,
  subject,
}: {
  user: ProfileUser;
  subject: ProfileSubject;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const history = useHistory();
  const { publicPlaylistLink } = usePageLinks(null);
  const query = useApiQuery("/webapp/private/users/:id/saved-playlists", {
    param: { id: user.id },
  });

  // Like on mobile, a reader with no bookmark has no section to see.
  if (query.state === "pending") return null;
  if (query.state === "error") return <QueryError />;
  if (query.body.length === 0) return null;

  return (
    <Section
      title={
        subject.kind === "me"
          ? t("Collections I saved")
          : t("{{name}}'s bookmarked playlists", {
              name: user.firstname === null ? "" : user.firstname,
            })
      }
      seeAll={{
        label: t("See all"),
        to:
          subject.kind === "me"
            ? "/profile/bookmarked-playlists"
            : `/profile/user/${user.id}/bookmarked-playlists`,
      }}
    >
      <CollectionGrid>
        {query.body.slice(0, 2).map((playlist) => (
          <SaveableCollectionCard
            key={playlist.id}
            playlist={playlist}
            listName={AnalyticsItemList.PROFILE}
            onOpen={() => {
              gaEventSelectItemPlaylist({
                playlist,
                listName: AnalyticsItemList.PROFILE,
              });
              history.push({
                pathname: publicPlaylistLink(playlist),
                state: { playlist },
              });
            }}
          />
        ))}
      </CollectionGrid>
    </Section>
  );
}

/** Playlists a dashboard shared with the viewer. Only ever their own. */
function SharedWithMeSection(): React.JSX.Element | null {
  const { t } = useTranslation();
  const history = useHistory();
  const { publicPlaylistLink } = usePageLinks(null);
  const query = useApiQuery("/webapp/private/playlists/shared-with-me", {
    query: { limit: 2, offset: 0 },
  });

  if (query.state === "pending") return null;
  if (query.state === "error") return <QueryError />;
  if (query.body.docs.length === 0) return null;

  return (
    <Section
      title={t("My shared playlists")}
      seeAll={{ label: t("See all"), to: "/profile/shared-playlist" }}
    >
      <CollectionGrid>
        {query.body.docs.map((playlist) => (
          <SaveableCollectionCard
            key={playlist.id}
            playlist={playlist}
            listName={AnalyticsItemList.PROFILE}
            onOpen={() => {
              gaEventSelectItemPlaylist({
                playlist,
                listName: AnalyticsItemList.PROFILE,
              });
              history.push({
                pathname: publicPlaylistLink(playlist),
                state: { playlist },
              });
            }}
          />
        ))}
      </CollectionGrid>
    </Section>
  );
}

function QueryError(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <p className="m-0 text-desktop-meta text-ds-strawberry-600">
      {t("Something went wrong. Please try again.")}
    </p>
  );
}
